"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Maximize2, Minimize2 } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { colorForDomain } from "./domainPalette";

type GraphItem = {
  id?: string;
  ref?: string;
  title?: string;
  domain?: string;
};

type GraphNode = {
  id: string;
  label: string;
  group: string;
  type: string;
  size?: number;
  details?: string;
  color?: string;
  x?: number;
  y?: number;
  z?: number;
};

type GraphLink = {
  source: string;
  target: string;
  value?: number;
};

export interface MCMGraphDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  center: GraphItem | null | undefined;
  items: GraphItem[];
  labelFor?: (domain?: string, title?: string, ref?: string, id?: string) => string;
  controls?: { id: string; label?: string; standard?: string }[];
}

export const MCMGraphDialog: React.FC<MCMGraphDialogProps> = ({ open, onOpenChange, center, items, labelFor, controls = [] }) => {
  const { isDark } = useTheme();
  const graphContainerRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const forceGraphRef = useRef<any>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const [initNonce, setInitNonce] = useState(0);
  const [highlightDomain, setHighlightDomain] = useState<string | null>(null);
  const suppressFitUntilRef = useRef<number>(0);


  // Domains present in this graph (for legend)
  const domainList = useMemo(() => {
    return Array.from(new Set(items.map(it => String(it.domain || 'Other')))).sort();
  }, [items]);

  const centerId = useMemo(() => {
    const ref = String(center?.ref || "").trim();
    if (ref) return ref;
    const id = String(center?.id || "").trim();
    if (!id) return "center";
    if (id.includes(":")) return id.split(":").pop() as string;
    return id;
  }, [center?.id, center?.ref]);

  const centerLabel = useMemo(() => {
    const t = labelFor ? labelFor(center?.domain, center?.title, center?.ref, center?.id) : (center?.title || center?.ref || center?.id || "");
    return String(t || "");
  }, [center?.domain, center?.title, center?.ref, center?.id, labelFor]);

  const domainGroup = (d?: string) => String(d || "Other");

  const pluralize = useCallback((name: string) => {
    const map: Record<string, string> = {
      Control: "Controls",
      Asset: "Assets",
      Supplier: "Suppliers",
      Policy: "Policies",
      Company: "Companies",
      Party: "Parties",
      Incident: "Incidents",
      Audit: "Audits",
      Finding: "Findings",
      Checklist: "Checklists",
      Objective: "Objectives",
      Issue: "Issues",
      Component: "Components",
      "Data Type": "Data Types",
      "Storage Location": "Storage Locations",
      "Interested Party": "Interested Parties",
      Other: "Others",
      Controls: "Controls",
    };
    if (map[name]) return map[name];
    if (/[^aeiou]y$/i.test(name)) return name.slice(0, -1) + "ies";
    if (name.endsWith("s")) return name;
    return name + "s";
  }, []);

  const graphData = useMemo(() => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];
    const centerNode: GraphNode = {
      id: `center:${centerId}`,
      label: centerLabel || "Current Component",
      group: "center",
      type: "Component",
      size: 12,
      color: "#a855f7", // purple-500 to match legend
    };
    nodes.push(centerNode);

    // Domain hubs
    const domains = Array.from(new Set(items.map(it => it.domain || ''))).sort();
    for (const dom of domains) {
      const nid = `domain:${dom}`;
      nodes.push({
        id: nid,
        label: pluralize(dom),
        group: "domain",
        type: "Linked",
        size: 10,
        color: "#9ca3af", // gray-400 to match legend
      });
      links.push({ source: `center:${centerId}`, target: nid, value: 3 });
    }

    // Item nodes linked to domain hubs
    for (const it of items) {
      const dom = domainGroup(it.domain);
      const nid = `${dom}:${String(it.id || it.ref)}`;
      const label = labelFor ? labelFor(it.domain, it.title, it.ref, it.id) : (it.title || it.ref || it.id || "");
      nodes.push({
        id: nid,
        label: String(label || ""),
        group: dom,
        type: String(it.ref || ""),
        size: 6,
        color: colorForDomain(dom),
      });
      links.push({ source: `domain:${dom}`, target: nid, value: 1 });
    }

    // Controls hub and control nodes
    if (controls && controls.length > 0) {
      nodes.push({
        id: "domain:Controls",
        label: "Controls",
        group: "domain",
        type: "Linked",
        size: 10,
      });
      links.push({ source: `center:${centerId}`, target: "domain:Controls", value: 3 });

      for (const c of controls || []) {
        const nid = `control:${String(c.id)}`;
        const sub = [String(c.id), c.standard ? String(c.standard) : ""].filter(Boolean).join(" ");
        nodes.push({ id: nid, label: String(c.label || c.id), group: "Controls", type: sub, size: 10, color: colorForDomain("Control") });
        links.push({ source: `domain:Controls`, target: nid, value: 2 });
      }
    }

    return { nodes, links };
  }, [items, centerId, centerLabel, center?.domain, labelFor, controls, pluralize]);

  const fitGraph = useCallback(() => {
    if (!forceGraphRef.current) return;
    try {
      forceGraphRef.current.zoomToFit(400, 60);
      return;
    } catch {}
    try {
      forceGraphRef.current.cameraPosition({ x: 0, y: 0, z: 220 }, { x: 0, y: 0, z: 0 }, 500);
    } catch {}
  }, []);

  // Highlight handler: dim nodes not in the selected domain and zoom to the domain items cluster
  const handleLegendClick = useCallback((domain: string | null) => {
    setHighlightDomain(prev => (prev === domain ? null : domain));
    if (!forceGraphRef.current) return;

    // Helper to dim a hex color using RGBA alpha
    const toRGBA = (hex: string, alpha: number) => {
      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!m) return hex;
      const r = parseInt(m[1], 16);
      const g = parseInt(m[2], 16);
      const b = parseInt(m[3], 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    if (!domain) {
      // Reset: restore colors and zoom out
      forceGraphRef.current.nodeColor((n: any) => n.color || undefined);
      try { forceGraphRef.current.refresh(); } catch {}
      suppressFitUntilRef.current = 0;
      fitGraph();
      return;
    }
    // Suppress automatic fit for a short period after applying highlight/zoom
    suppressFitUntilRef.current = Date.now() + 1500;

    // Per-node dimming via color alpha (keeps center, hubs, and controls fully visible)
    forceGraphRef.current.nodeColor((n: any) => {
      const base = n.color || '#9ca3af';
      const keep = n.id === `center:${centerId}` || String(n.id).startsWith('domain:') || String(n.id).startsWith('control:');
      if (keep) return base;
      const nodeDomain = String(n.group || '').toLowerCase();
      return nodeDomain === domain.toLowerCase() ? base : toRGBA(base, 0.15);
    });
    try { forceGraphRef.current.refresh(); } catch {}

    // Wait a frame for positions to settle, then zoom to the domain items cluster
    setTimeout(() => {
      if (!forceGraphRef.current) return;
      const gData = forceGraphRef.current.graphData();
      const domainNodes = gData.nodes.filter((n: any) => {
        if (String(n.id).startsWith('domain:') || String(n.id).startsWith('control:') || n.id === `center:${centerId}`) return false;
        const nodeDomain = String(n.group || '').toLowerCase();
        return nodeDomain === domain.toLowerCase();
      });
      if (domainNodes.length > 0) {
        const avgX = domainNodes.reduce((sum: number, n: any) => sum + (n.x || 0), 0) / domainNodes.length;
        const avgY = domainNodes.reduce((sum: number, n: any) => sum + (n.y || 0), 0) / domainNodes.length;
        const avgZ = domainNodes.reduce((sum: number, n: any) => sum + ((typeof n.z === 'number' ? n.z : 0) || 0), 0) / domainNodes.length;
        // Compute 85th percentile spread from centroid to reduce sensitivity to outliers
        const dists = domainNodes.map((n: any) => {
          const dx = (n.x || 0) - avgX;
          const dy = (n.y || 0) - avgY;
          const dz = ((typeof n.z === 'number' ? n.z : 0) || 0) - avgZ;
          return Math.sqrt(dx*dx + dy*dy + dz*dz);
        }).sort((a: number, b: number) => a - b);
        const idx = Math.max(0, Math.min(dists.length - 1, Math.floor(dists.length * 0.85)));
        const spread = dists[idx] || 0;
        // Keep the camera further back overall
        const zDist = Math.max(220, Math.min(600, spread * 3.0 + 220));
        try {
          // Position the camera at a relative distance along +Z from the cluster center
          forceGraphRef.current.cameraPosition({ x: avgX, y: avgY, z: avgZ + zDist }, { x: avgX, y: avgY, z: avgZ }, 800);
        } catch {}
      }
    }, 100);
  }, [centerId, fitGraph]);

  const toggleFullscreen = useCallback(async () => {
    const host = rootRef.current;
    if (!host) return;
    try {
      if (!document.fullscreenElement) {
        await (host as any).requestFullscreen();
        setIsFullscreen(true);
        setTimeout(() => { try { fitGraph(); } catch {} }, 200);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!open) return;
    if (forceGraphRef.current) return;
    let mounted = true;
    let ro: ResizeObserver | null = null;
    let timer: number | null = null;
    let raf: number | null = null;
    let interval: number | null = null;

    const tryInit = async () => {
      if (!mounted) return false;
      if (!graphContainerRef.current) return false;
      if (forceGraphRef.current) return true;
      const rect = graphContainerRef.current.getBoundingClientRect();
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
      const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
      const width = Math.floor(rect.width) || graphContainerRef.current.offsetWidth || graphContainerRef.current.clientWidth || Math.max(320, Math.floor(vw * 0.8));
      const height = Math.floor(rect.height) || graphContainerRef.current.offsetHeight || graphContainerRef.current.clientHeight || Math.max(320, Math.floor(vh * 0.7));
      try {
        const ForceGraph3D: any = (await import("3d-force-graph")).default;
        if (!mounted || !graphContainerRef.current) return false;
        const Graph: any = ForceGraph3D();
        const inst = Graph(graphContainerRef.current);
        inst
          .width(width)
          .height(height)
          .showNavInfo(false)
          .nodeRelSize(6)
          .nodeOpacity(0.9)
          .linkOpacity(0.85)
          .linkWidth(2)
          .linkDirectionalParticles(4)
          .linkDirectionalParticleWidth(2)
          .nodeColor((n: any) => n.color || undefined)
          .nodeVal((n: GraphNode) => n.size ?? 4)
          .nodeLabel((n: GraphNode) => `<div style="max-width:240px"><strong>${n.label}</strong><br/><span style=\"font-size:12px;opacity:0.8\">${n.type}</span></div>`);
        inst.d3Force("charge")?.strength(-140);
        try {
          const l = inst.d3Force("link");
          if (l && typeof l.distance === "function") {
            l.distance((lnk: any) => {
              const sid = String((lnk?.source && (lnk.source.id ?? lnk.source)) || "");
              const tid = String((lnk?.target && (lnk.target.id ?? lnk.target)) || "");
              if (sid.startsWith("center:")) return 140;
              if (sid.startsWith("domain:")) return 90;
              if (tid.startsWith("domain:")) return 90;
              return 60;
            });
          }
        } catch {}
        forceGraphRef.current = inst;
        inst.graphData(graphData);
        const bg = isDark ? "rgba(0,0,0,0)" : "rgba(255,255,255,0)";
        inst.backgroundColor(bg);
        inst.linkColor(() => (isDark ? "rgba(96,165,250,0.9)" : "rgba(37,99,235,0.75)"));
        try { inst.refresh(); } catch {}
        setTimeout(() => { try { inst.refresh(); fitGraph(); } catch {} }, 160);
        setInitialised(true);
        return true;
      } catch {
        return false;
      }
    };

    const start = () => {
      const loop = async () => {
        if (!mounted) return;
        if (!graphContainerRef.current) {
          raf = requestAnimationFrame(loop);
          return;
        }
        const done = await tryInit();
        if (done || !mounted) return;
        ro = new ResizeObserver(async () => {
          const ok = await tryInit();
          if (ok && ro) ro.disconnect();
        });
        ro.observe(graphContainerRef.current as Element);
        timer = window.setTimeout(async () => {
          const ok = await tryInit();
          if (ok && ro) ro.disconnect();
        }, 300);
        let attempts = 0;
        interval = window.setInterval(async () => {
          attempts += 1;
          const ok = await tryInit();
          if (ok || attempts > 40) {
            if (interval) { try { window.clearInterval(interval); } catch {} interval = null; }
            if (ok && ro) ro.disconnect();
          }
        }, 200);
      };
      raf = requestAnimationFrame(loop);
    };

    start();

    return () => {
      mounted = false;
      if (ro) {
        try { ro.disconnect(); } catch {}
      }
      if (timer) {
        try { window.clearTimeout(timer); } catch {}
      }
      if (interval) {
        try { window.clearInterval(interval); } catch {}
      }
      if (raf) {
        try { cancelAnimationFrame(raf); } catch {}
      }
      if (forceGraphRef.current) {
        try { forceGraphRef.current._destructor?.(); } catch {}
        forceGraphRef.current = null;
      }
      if (graphContainerRef.current) {
        graphContainerRef.current.innerHTML = "";
      }
      setInitialised(false);
    };
  }, [open, initNonce]);

  useEffect(() => {
    if (!open) return;
    if (forceGraphRef.current) forceGraphRef.current.graphData(graphData);
  }, [open, graphData]);

  useEffect(() => {
    if (!open) return;
    if (forceGraphRef.current) {
      const bg = isDark ? "rgba(0,0,0,0)" : "rgba(255,255,255,0)";
      forceGraphRef.current.backgroundColor(bg);
      forceGraphRef.current.linkColor(() => (isDark ? "rgba(96,165,250,0.9)" : "rgba(37,99,235,0.75)"));
    }
  }, [open, isDark]);

  useEffect(() => {
    if (!open) return;
    if (!forceGraphRef.current || !graphContainerRef.current) return;
    const el = graphContainerRef.current;
    const t = window.setTimeout(() => {
      const rect = el.getBoundingClientRect();
      const w = Math.floor(rect.width) || el.offsetWidth || el.clientWidth;
      const h = Math.floor(rect.height) || el.offsetHeight || el.clientHeight;
      if (w && h) {
        try { forceGraphRef.current.width(w).height(h).refresh(); } catch {}
      }
    }, 350);
    return () => { try { window.clearTimeout(t); } catch {} };
  }, [isFullscreen, open]);
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (!forceGraphRef.current || !graphContainerRef.current) return;
      const el = graphContainerRef.current;
      const rect = el.getBoundingClientRect();
      const w = Math.floor(rect.width) || el.offsetWidth || el.clientWidth;
      const h = Math.floor(rect.height) || el.offsetHeight || el.clientHeight;
      if (w && h) {
        try { forceGraphRef.current.width(w).height(h); } catch {}
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  useEffect(() => {
    if (!open || !initialised) return;
    if (!graphContainerRef.current || !forceGraphRef.current) return;
    const obs = new ResizeObserver(() => {
      const el = graphContainerRef.current as HTMLDivElement;
      const rect = el.getBoundingClientRect();
      const w = Math.floor(rect.width) || el.offsetWidth || el.clientWidth;
      const h = Math.floor(rect.height) || el.offsetHeight || el.clientHeight;
      if (w && h) {
        try {
          // Only adjust canvas size and refresh; do not auto-fit here to avoid camera jumps
          forceGraphRef.current.width(w).height(h).refresh();
        } catch {}
      }
    });
    obs.observe(graphContainerRef.current as Element);
    return () => { try { obs.disconnect(); } catch {} };
  }, [open, initialised, fitGraph, highlightDomain]);

  const handleOpenChange = useCallback((v: boolean) => {
    onOpenChange(v);
    if (v) {
      setTimeout(() => setInitNonce((n) => n + 1), 0);
    }
  }, [onOpenChange]);

  const containerStyle = useMemo(() => ({ height: isFullscreen ? "calc(100vh - 52px)" : "clamp(300px, 42vh, 520px)" }), [isFullscreen]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent forceMount className="w-full p-0 bg-card mcm-graph-card sm:max-w-3xl lg:max-w-5xl" onOpenAutoFocus={() => setInitNonce((n) => n + 1)}>
        <div ref={rootRef} className="flex flex-col w-full">
          <DialogHeader className="px-4 py-3">
            <div className="flex items-center justify-between">
              <DialogTitle>Linked Components</DialogTitle>
              <Button variant="outline" size="icon" onClick={toggleFullscreen} className="shrink-0">
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>
          </DialogHeader>
          <div className="relative w-full" style={containerStyle as any} onTransitionEnd={(e) => {
            // Ignore transitionend events bubbling from children (e.g., legend hover color transitions)
            if (e.target !== e.currentTarget) return;
            // Only react to container width/height transitions
            const prop = (e as React.TransitionEvent<HTMLDivElement>).propertyName;
            if (!prop || (prop !== 'height' && prop !== 'width')) return;
            if (!forceGraphRef.current || !graphContainerRef.current) return;
            const el = graphContainerRef.current;
            const rect = el.getBoundingClientRect();
            const w = Math.floor(rect.width) || el.offsetWidth || el.clientWidth;
            const h = Math.floor(rect.height) || el.offsetHeight || el.clientHeight;
            if (w && h) {
              try {
                // Only adjust canvas size and refresh; do not auto-fit here to avoid camera jumps
                forceGraphRef.current.width(w).height(h).refresh();
              } catch {}
            }
          }}>
            {/* Legend */}
            <div
              className="absolute top-2 right-2 z-10 p-2 rounded-md backdrop-blur-sm border dark:bg-white/5 dark:border-white/10 bg-white/90 border-gray-200 shadow-sm dark:shadow-none text-xs space-y-1 max-w-[180px]"
              onMouseEnter={() => { suppressFitUntilRef.current = Date.now() + 4000; }}
              onMouseLeave={() => { suppressFitUntilRef.current = Date.now() + 600; }}
            >
              <button
                type="button"
                className="font-semibold text-foreground mb-1 cursor-pointer hover:opacity-70 transition-opacity"
                onClick={() => handleLegendClick(null)}
                title="Reset highlight"
              >
                Legend
              </button>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span className="text-foreground">Center</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gray-400" />
                <span className="text-foreground">Domain Hubs</span>
              </div>
              {domainList.map((dom) => (
                <button
                  key={dom}
                  type="button"
                  className={`flex items-center gap-2 w-full hover:bg-gray-100 dark:hover:bg-white/5 rounded px-1 py-0.5 transition-colors ${highlightDomain === dom ? 'bg-gray-200 dark:bg-white/10' : ''}`}
                  onClick={() => handleLegendClick(dom)}
                  title={`Highlight ${dom}`}
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorForDomain(dom) }} />
                  <span className="text-foreground text-left truncate">{dom}</span>
                </button>
              ))}
              {controls && controls.length > 0 && (
                <button
                  type="button"
                  className={`flex items-center gap-2 w-full hover:bg-gray-100 dark:hover:bg-white/5 rounded px-1 py-0.5 transition-colors ${highlightDomain === 'Controls' ? 'bg-gray-100 dark:bg-white/10' : ''}`}
                  onClick={() => handleLegendClick('Controls')}
                  title="Highlight Controls"
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorForDomain('Control') }} />
                  <span className="text-foreground text-left truncate">Controls</span>
                </button>
              )}
            </div>
            <div key={initNonce} ref={graphContainerRef} className="h-full w-full" />
            {!initialised && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/70">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
;

export default MCMGraphDialog;
